import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Award, Shield, Users, Heart } from 'lucide-react';

const TrustSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    { value: '20,000+', label: 'Active Users', icon: Users },
    { value: '100%', label: 'DPDP Compliant', icon: Shield },
    { value: '24/7', label: 'Care Support', icon: Heart },
    { value: '2M+', label: 'Social Reach', icon: Award }
  ];

  const founders = [
    {
      name: 'Dr. Kafeel Khan',
      role: 'Co-Founder & Clinical Lead',
      credentials: '2M+ Social Media Followers',
      description: 'Pediatric strategy anchor with grassroots healthcare trust. Authored "The Gorakhpur tragedy" memoir that inspired the Bollywood film Jawan starring Shah Rukh Khan.'
    },
    {
      name: 'Dr. Shabista Khan',
      role: 'Co-Founder',
      credentials: 'Medical Operations Lead',
      description: 'Responsible for clinical quality control and oversight of the medical advisory panel, ensuring highest standards of care.'
    },
    {
      name: 'Bilal Zaidi',
      role: 'Digital Inclusion Lab',
      credentials: 'Award-winning Entrepreneur',
      description: 'Leading operational execution, business scaling, tech infrastructure, and brand architecture for Clan Health.'
    }
  ];

  const advisors = [
    'Eminent Doctor Advisory Panel - Vetted Specialists in Pediatrics',
    'Geriatric Care Specialists - Chronic Disease Management Experts',
    'Metabolic Health Experts - Validating Clinical Knowledge Bases'
  ];

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section ref={ref} className="relative py-32 px-6 lg:px-8 bg-slate-900 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <motion.div
        className="absolute top-1/4 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Trusted by Families.
            <span className="block mt-2 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Built by Experts.
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Healthcare is personal. We take security, privacy, and care quality seriously.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 text-center group hover:border-teal-500/50 transition-all duration-300"
              data-testid={`trust-stat-card-${index + 1}`}
            >
              <stat.icon className="h-10 w-10 text-teal-400 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-4xl font-bold text-white mb-2" data-testid={`trust-stat-value-${index + 1}`}>{stat.value}</div>
              <div className="text-slate-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Founders */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="mb-20"
        >
          <h3 className="text-3xl font-bold text-white mb-12 text-center">Meet Our Founders</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {founders.map((founder, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 hover:border-teal-500/50 transition-all duration-300"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-full mb-6 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{founder.name.charAt(0)}</span>
                </div>
                <h4 className="text-2xl font-bold text-white mb-2">{founder.name}</h4>
                <p className="text-teal-400 font-semibold mb-2">{founder.role}</p>
                <p className="text-slate-400 text-sm mb-4">{founder.credentials}</p>
                <p className="text-slate-300 leading-relaxed">{founder.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advisory Board */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="bg-slate-950/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-2xl font-bold text-white mb-6">Medical Advisory Board</h3>
          <ul className="space-y-4">
            {advisors.map((advisor, index) => (
              <li key={index} className="flex items-center gap-3 text-slate-300">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span>{advisor}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSection;